import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';

import styles from './styles/Tabs.module.scss';

const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva('', {
  variants: {
    variant: {
      default: styles.tabsList,
      block: styles.tabsList,
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const tabsTriggerVariants = cva('', {
  variants: {
    variant: {
      default: styles.tabsTrigger,
      block: styles.tabsTrigger,
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  fullWidth?: boolean;
  variant?: 'default' | 'block';
}

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, fullWidth = false, variant = 'default', ...props }, ref) => {
    if (variant === 'block') {
      return (
        <div className={clsx(tabsListVariants({ variant }), className)} data-cxy-name="tabs" data-variant={variant}>
          <div>
            <TabsPrimitive.List ref={ref} {...props} />
          </div>
        </div>
      );
    }

    return (
      <TabsPrimitive.List
        className={clsx(tabsListVariants({ variant }), className, { [styles.fullWidth]: fullWidth })}
        data-variant={variant}
        ref={ref}
        {...props}
      />
    );
  },
);
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  variant?: 'default' | 'block';
}

const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <TabsPrimitive.Trigger
      className={clsx(tabsTriggerVariants({ variant }), className)}
      data-variant={variant}
      ref={ref}
      {...props}
    />
  ),
);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content className={clsx(styles.tabsContent, className)} ref={ref} {...props} />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
